import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertTriangle, Eraser } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/apiClient";

const SCOPES = [
  { key: "invoices", label: "Nur Ausgangsrechnungen", hint: "Rechnungen + deren Buchungen. Bank-Zahlungen bleiben, werden nur entkoppelt – beim erneuten Hochladen per QR-Referenz automatisch wieder zugeordnet." },
  { key: "receipts", label: "Nur Belege & Quittungen", hint: "Belege + deren Buchungen. Bank-Zahlungen bleiben, werden nur entkoppelt." },
  { key: "bills", label: "Nur Eingangsrechnungen", hint: "Lieferantenrechnungen + deren Buchungen; Bank-Zahlungen entkoppelt." },
  { key: "inbox", label: "Nur Posteingang", hint: "Alle Posteingangs-Belege (offen und erledigt) inkl. Dateien." },
  { key: "bank", label: "Nur Bank-Transaktionen & Importe", hint: "Alle Bankbuchungen und Import-Läufe. Belege/Rechnungen bleiben und gelten wieder als nicht zugeordnet bzw. offen." },
  { key: "payroll", label: "Nur Personal & Lohn", hint: "Mitarbeitende, Lohnläufe, Lohnabrechnungs-Archiv, Zeiterfassung, App-Stunden/Boni." },
];

export const ResetAccountingDialog = ({ open, onOpenChange, company }) => {
  const [confirmText, setConfirmText] = useState("");
  const [working, setWorking] = useState(false);
  const [mode, setMode] = useState("select"); // select | all
  const [scopes, setScopes] = useState([]);

  const toggle = (k) => setScopes((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const effective = mode === "all" ? ["all"] : scopes;
  const canRun = confirmText === "RESET" && effective.length > 0 && !working;

  const runReset = async () => {
    if (!canRun || !company?._id) return;
    setWorking(true);
    try {
      const r = await apiClient.post(`/companies/${company._id}/reset-accounting`, { confirm: "RESET", scopes: effective });
      const d = r.data || {};
      toast.success(mode === "all" ? `Buchhaltung zurückgesetzt — ${d.total_documents || 0} Einträge gelöscht` : `Gelöscht: ${SCOPES.filter((s) => scopes.includes(s.key)).map((s) => s.label.replace("Nur ", "")).join(", ")} — ${d.total_documents || 0} Einträge`, { duration: 6000 });
      onOpenChange(false);
      setConfirmText("");
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset fehlgeschlagen");
    } finally {
      setWorking(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!working) { onOpenChange(o); setConfirmText(""); } }}>
      <DialogContent className="max-w-lg" data-testid="reset-accounting-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <AlertTriangle className="h-5 w-5" />
            Daten löschen – {company?.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="flex gap-2 text-xs">
            <button type="button" onClick={() => setMode("select")} className={`rounded-full px-3 py-1 border ${mode === "select" ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-700"}`} data-testid="reset-mode-select">Nur Ausgewähltes löschen</button>
            <button type="button" onClick={() => setMode("all")} className={`rounded-full px-3 py-1 border ${mode === "all" ? "bg-rose-700 text-white border-rose-700" : "border-slate-300 text-slate-700"}`} data-testid="reset-mode-all">Alles zurücksetzen</button>
          </div>
          {mode === "select" ? (
            <div className="space-y-1.5 max-h-64 overflow-auto pr-1">
              {SCOPES.map((s) => (
                <label key={s.key} className={`flex items-start gap-2 rounded-md border px-2.5 py-2 cursor-pointer ${scopes.includes(s.key) ? "border-rose-300 bg-rose-50" : "border-slate-200 hover:bg-slate-50"}`} data-testid={`reset-scope-${s.key}`}>
                  <input type="checkbox" className="mt-0.5" checked={scopes.includes(s.key)} onChange={() => toggle(s.key)} data-testid={`reset-scope-${s.key}-checkbox`} />
                  <span>
                    <span className="font-medium text-slate-800">{s.label}</span>
                    <span className="block text-[11px] text-slate-500">{s.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          ) : (
            <ul className="list-disc list-inside text-xs text-slate-600 space-y-0.5">
              <li>Alle Belege, Eingangs- &amp; Ausgangsrechnungen</li>
              <li>Das komplette Journal (alle Buchungen)</li>
              <li>Alle Bank-Importe &amp; Payrexx-Berichte, der gesamte Posteingang</li>
              <li>Alle Mitarbeitenden, Lohnläufe, Lohnabrechnungen, Zeiterfassung, App-Stunden/Boni</li>
            </ul>
          )}
          <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5">
            Bleiben immer erhalten: Firma, Kontenplan, Kontakte, Lohn-Einstellungen, Vorlagen, KI-Lernregeln &amp; gelernte Bank-Zuordnungen.
          </p>
          <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5">
            ⚠️ Kein Backup — diese Aktion kann nicht rückgängig gemacht werden.
          </p>
          <div>
            <label className="text-xs font-medium text-slate-700">
              Zur Bestätigung <span className="font-mono bg-slate-100 px-1 rounded">RESET</span> eintippen:
            </label>
            <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="RESET" className="mt-1 font-mono" data-testid="reset-confirm-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={working} data-testid="reset-cancel-button">Abbrechen</Button>
          <Button variant="destructive" onClick={runReset} disabled={!canRun} data-testid="reset-execute-button">
            <Eraser className="h-4 w-4 mr-1.5" />
            {working ? "Lösche…" : mode === "all" ? "Alles endgültig löschen" : `Ausgewähltes löschen${scopes.length ? ` (${scopes.length})` : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
